import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { toPng } from 'html-to-image';
import { api } from '@/App';
import { Download, Share2 } from 'lucide-react';

const getImageUrl = (p) => {
  if (!p) return null;
  if (p.startsWith('data:') || p.startsWith('http')) return p;
  if (p.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${p}`;
  return p;
};

const StatTile = ({ label, value, testId }) => (
  <div className="bg-white/10 rounded-xl px-3 py-2.5 text-center" data-testid={testId}>
    <div className="text-2xl font-extrabold text-white leading-tight">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-white/70 mt-0.5">{label}</div>
  </div>
);

export const SeasonRecapDialog = ({ open, onClose }) => {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setRecap(null);
      setPhotoError(false);
      return;
    }
    setLoading(true);
    api.get('/users/me/season-recap')
      .then((r) => setRecap(r.data))
      .catch(() => toast.error('Failed to load your season recap'))
      .finally(() => setLoading(false));
  }, [open]);

  const renderPng = () => toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await renderPng();
      const link = document.createElement('a');
      link.download = `nlv-season-recap-${recap?.year || ''}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Recap image downloaded!');
    } catch (e) {
      console.error(e);
      toast.error('Could not generate the image. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await renderPng();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `nlv-season-recap-${recap?.year || ''}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `My ${recap?.year} NL Volleyball season`,
          text: `My ${recap?.year} season with NL Volleyball 🏐`,
        });
      } else {
        // Fallback: download instead
        const link = document.createElement('a');
        link.download = file.name;
        link.href = dataUrl;
        link.click();
        toast.info('Sharing not supported on this device — image downloaded instead.');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.error(e);
        toast.error('Could not share the image.');
      }
    } finally {
      setBusy(false);
    }
  };

  const participates = recap?.rating_participation_enabled !== false;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="season-recap-dialog">
        <DialogHeader>
          <DialogTitle>🎉 Season Recap</DialogTitle>
          <DialogDescription>Your {recap?.year || new Date().getFullYear()} season at a glance — download or share it!</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-gray-500">Building your recap...</div>}

        {recap && !loading && (
          <>
            <div
              ref={cardRef}
              className="rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-700 via-purple-700 to-orange-600 p-5 sm:p-6"
              data-testid="season-recap-card"
            >
              <div className="flex flex-col items-center gap-2 mb-4">
                <img
                  src="/nlv-logo.png"
                  alt="NL Volleyball"
                  crossOrigin="anonymous"
                  className="h-14 w-auto rounded-xl ring-2 ring-white/40 drop-shadow-md"
                  data-testid="recap-logo"
                />
                <div className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-full">{recap.year} SEASON</div>
              </div>

              <div className="flex items-center gap-3 mb-5">
                {recap.photo && !photoError ? (
                  <img
                    src={getImageUrl(recap.photo)}
                    alt={recap.name}
                    crossOrigin="anonymous"
                    className="w-14 h-14 rounded-full object-cover border-2 border-white/60"
                    onError={() => setPhotoError(true)}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">
                    {recap.name?.[0]}
                  </div>
                )}
                <div>
                  <div className="text-white font-bold text-xl leading-tight" data-testid="recap-player-name">{recap.name}</div>
                  <div className="text-white/70 text-xs">{recap.full_name}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 mb-4">
                <StatTile label="Games Played" value={recap.games_played} testId="recap-games-played" />
                <StatTile label="MVP Wins 🏆" value={recap.mvp_wins} testId="recap-mvp-wins" />
                {participates && recap.total_kudos !== null && (
                  <StatTile label="Kudos Received" value={recap.total_kudos} testId="recap-total-kudos" />
                )}
                {participates && recap.best_game_rating !== null && (
                  <StatTile label="Best Game Rating" value={`${recap.best_game_rating}`} testId="recap-best-rating" />
                )}
                {participates && recap.community_rating !== null && (
                  <StatTile label="Community Rating" value={`${recap.community_rating}`} testId="recap-community-rating" />
                )}
                {participates && recap.standing && (
                  <StatTile label="Standing" value={`${recap.standing.rank_label} / ${recap.standing.total}`} testId="recap-standing" />
                )}
              </div>

              {participates && recap.top_kudos_label && (
                <div className="bg-white/10 rounded-xl px-3 py-2 mb-4 text-center" data-testid="recap-top-kudos">
                  <span className="text-white/70 text-xs uppercase tracking-wide">Top Kudos </span>
                  <span className="text-white font-bold text-sm">{recap.top_kudos_label}</span>
                </div>
              )}

              <div className="text-center text-white font-semibold text-sm sm:text-base italic" data-testid="recap-highlight">
                “{recap.highlight}”
              </div>

              <div className="text-center text-white/50 text-[10px] mt-4">nlvolleyball.nl • {recap.year} Season Recap</div>
            </div>

            <div className="flex gap-3 mt-1">
              <Button
                onClick={handleDownload}
                disabled={busy}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                data-testid="recap-download-btn"
              >
                <Download className="w-4 h-4 mr-2" /> {busy ? 'Working...' : 'Download PNG'}
              </Button>
              <Button
                onClick={handleShare}
                disabled={busy}
                variant="outline"
                className="flex-1"
                data-testid="recap-share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SeasonRecapDialog;
